import { identity, merge } from 'rxjs';
import { isKarmaTestEnv } from '../../helpers/env';
import { retryDelay } from '../../helpers/retryDelay';
import { Service } from '../../services/base/service';

export interface Editor<S extends EditorServices> {
  /** Services that make up this editor. */
  readonly services: S;
}

export type EditorServices = {
  [key: string]: Service;
};

export interface EditorSpec<S extends EditorServices> {
  /** Services to be made available on the editor's services property. */
  services: S;

  /**
   * Additional services/dependencies whose side effects to mix in.
   *
   * Private services get the lowest priority, unless they are also a dependency of one of the public
   * services.
   */
  privateServices?: Service[];
}

export function createEditor<S extends EditorServices>(spec: EditorSpec<S>): Editor<S> {
  return new BaseEditor(spec);
}

export class BaseEditor<S extends EditorServices> implements Editor<S> {
  constructor(spec: EditorSpec<S>) {
    this.services = spec.services;

    const publicServices = Object.values(spec.services);
    const privateServices = spec.privateServices || [];
    const allServices = [...publicServices, ...privateServices];
    const effects$ = merge(...allServices.map(service => service.effects$));
    const effectErrorOperator = isKarmaTestEnv ? identity : retryDelay<void>();

    // Start performing side effects. This subscription will be garbage collected as long as all the underlying
    // streams are garbage collectable. (Such as BehaviorSubjects, EMPTY, etc.)
    effects$.pipe(effectErrorOperator).subscribe();
  }

  readonly services: S;
}
